import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Disclaimer } from "../components/Disclaimer";
import { debtPayoffSchedule } from "../engine/calculations";
import { formatINRCompact } from "../lib/currency";

const STRATEGIES = [
  { key: "avalanche", label: "Avalanche", blurb: "Highest interest rate first — minimises total interest." },
  { key: "snowball", label: "Snowball", blurb: "Smallest balance first — fastest early wins." },
  { key: "hybrid", label: "Hybrid", blurb: "Clear small \"quick win\" debts first, then avalanche." },
];

/**
 * Return the debts in the ORDER THE STRATEGY WILL ATTACK THEM — i.e. what a user reading
 * "Avalanche" expects to see (highest rate at the top, always). This mirrors
 * `debtPriorityOrder()` in engine/calculations.js but runs off the raw input debts so we
 * can show the intent even before the simulation has computed anything. Previously the UI
 * displayed the ENGINE's `payoffOrder` (order in which balances hit zero during the sim),
 * which for edge cases (small low-rate debt + huge high-rate debt) could clear the small
 * one first and mislead the user into thinking Avalanche picked it.
 */
function strategyPriorityOrder(debts, strategy, threshold) {
  const clean = debts
    .map((d) => ({
      name: d.name || "Debt",
      balance: Number(d.balance) || 0,
      rate: Number(d.rate) || 0,
    }))
    .filter((d) => d.balance > 0);
  if (strategy === "avalanche") return [...clean].sort((a, b) => b.rate - a.rate);
  if (strategy === "snowball") return [...clean].sort((a, b) => a.balance - b.balance);
  if (strategy === "hybrid") {
    const quick = clean.filter((d) => d.balance <= threshold).sort((a, b) => a.balance - b.balance);
    const rest = clean.filter((d) => d.balance > threshold).sort((a, b) => b.rate - a.rate);
    return [...quick, ...rest];
  }
  return clean;
}

let nextId = 3;

function emptyDebt(id, seed) {
  return { id, name: seed?.name || `Debt ${id}`, balance: seed?.balance ?? 50000, rate: seed?.rate ?? 18, minPayment: seed?.minPayment ?? 2000 };
}

/**
 * Debt Freedom — a dedicated tool (not a config-driven calculator) per CLAUDE.md: it needs a
 * variable-length list of debts and a three-way strategy comparison, which the generic
 * single-form calculator framework isn't shaped for.
 */
export function DebtFreedomPage() {
  const navigate = useNavigate();
  const [debts, setDebts] = useState([
    emptyDebt(1, { name: "Credit Card", balance: 80000, rate: 36, minPayment: 3000 }),
    emptyDebt(2, { name: "Personal Loan", balance: 200000, rate: 14, minPayment: 6000 }),
  ]);
  const [monthlyBudget, setMonthlyBudget] = useState(15000);
  const [hybridThreshold, setHybridThreshold] = useState(50000);

  function updateDebt(id, key, value) {
    setDebts((prev) => prev.map((d) => (d.id === id ? { ...d, [key]: value } : d)));
  }

  function addDebt() {
    nextId += 1;
    setDebts((prev) => [...prev, emptyDebt(nextId)]);
  }

  function removeDebt(id) {
    setDebts((prev) => prev.filter((d) => d.id !== id));
  }

  const totalMin = useMemo(() => debts.reduce((sum, d) => sum + (Number(d.minPayment) || 0), 0), [debts]);
  const budgetTooLow = Number(monthlyBudget) < totalMin;

  const results = useMemo(() => {
    if (debts.length === 0 || budgetTooLow) return null;
    const engineDebts = debts.map((d) => ({
      name: d.name || "Debt",
      balance: Number(d.balance) || 0,
      rate: (Number(d.rate) || 0) / 100,
      minPayment: Number(d.minPayment) || 0,
    }));
    try {
      return STRATEGIES.map((s) => ({
        ...s,
        result: debtPayoffSchedule(engineDebts, Number(monthlyBudget) || 0, s.key, Number(hybridThreshold) || 0),
      }));
    } catch {
      return null;
    }
  }, [debts, monthlyBudget, hybridThreshold, budgetTooLow]);

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="text-sm text-muted hover:text-gold"
        data-testid="page-back"
      >
        &larr; Go back
      </button>
      <h1 className="mt-3 text-2xl font-semibold text-text">Debt Freedom</h1>
      <p className="mb-6 text-sm text-muted">
        Compare avalanche, snowball, and hybrid strategies for clearing your debts.
      </p>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Your debts</CardTitle>
          <CardDescription>Add every debt you're carrying — name, balance, rate, and minimum payment.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {debts.map((debt) => (
            <div key={debt.id} className="grid grid-cols-1 gap-2 rounded-md border border-border p-3 sm:grid-cols-[2fr_1fr_1fr_1fr_auto] sm:items-end">
              <div className="flex flex-col gap-1">
                <Label>Name</Label>
                <Input value={debt.name} onChange={(e) => updateDebt(debt.id, "name", e.target.value)} />
              </div>
              <div className="flex flex-col gap-1">
                <Label>Balance</Label>
                <Input type="number" value={debt.balance} onChange={(e) => updateDebt(debt.id, "balance", e.target.value)} />
              </div>
              <div className="flex flex-col gap-1">
                <Label>Rate (%)</Label>
                <Input type="number" value={debt.rate} onChange={(e) => updateDebt(debt.id, "rate", e.target.value)} />
              </div>
              <div className="flex flex-col gap-1">
                <Label>Min payment</Label>
                <Input type="number" value={debt.minPayment} onChange={(e) => updateDebt(debt.id, "minPayment", e.target.value)} />
              </div>
              <Button variant="outline" size="sm" onClick={() => removeDebt(debt.id)} aria-label={`Remove ${debt.name}`}>
                <Trash2 size={16} />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addDebt} className="w-fit gap-2">
            <Plus size={16} /> Add debt
          </Button>

          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <Label>Total monthly repayment budget</Label>
              <Input type="number" value={monthlyBudget} onChange={(e) => setMonthlyBudget(e.target.value)} />
              {budgetTooLow && (
                <p className="text-xs text-red-400">
                  Must be at least {formatINRCompact(totalMin)} (sum of minimum payments).
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <Label>Hybrid "quick win" threshold</Label>
              <Input type="number" value={hybridThreshold} onChange={(e) => setHybridThreshold(e.target.value)} />
              <p className="text-xs text-muted">Debts at or below this balance get cleared first under Hybrid.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {results ? (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            {results.map(({ key, label, blurb, result }) => {
              const priority = strategyPriorityOrder(debts, key, Number(hybridThreshold) || 0);
              const orderLabel =
                key === "avalanche"
                  ? "Pay in this order (highest rate first)"
                  : key === "snowball"
                    ? "Pay in this order (smallest balance first)"
                    : "Pay in this order (quick wins, then rate)";
              return (
              <Card key={key} data-testid={`debt-strategy-${key}`}>
                <CardHeader>
                  <CardTitle className="text-base">{label}</CardTitle>
                  <CardDescription>{blurb}</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted">Months to debt-free</span>
                    <span className="font-semibold text-gold">
                      {result.monthsToDebtFree ?? "100y+"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted">Total interest paid</span>
                    <span className="font-medium">{formatINRCompact(result.totalInterest)}</span>
                  </div>
                  <div>
                    <span className="text-muted">{orderLabel}</span>
                    <ol className="ml-4 list-decimal" data-testid={`debt-priority-order-${key}`}>
                      {priority.map((d) => (
                        <li key={d.name}>
                          {d.name}
                          <span className="ml-1 text-xs text-muted">
                            · {Number(d.rate).toFixed(2)}% · {formatINRCompact(d.balance)}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-base">Side-by-side comparison</CardTitle>
            </CardHeader>
            <CardContent>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted">
                    <th className="py-1">Strategy</th>
                    <th className="py-1">Months</th>
                    <th className="py-1">Total interest</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map(({ key, label, result }) => (
                    <tr key={key} className="border-t border-border">
                      <td className="py-1.5">{label}</td>
                      <td className="py-1.5">{result.monthsToDebtFree ?? "100y+"}</td>
                      <td className="py-1.5">{formatINRCompact(result.totalInterest)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Disclaimer className="mt-6" />
        </>
      ) : (
        !budgetTooLow && <p className="text-sm text-muted">Add at least one debt to see results.</p>
      )}
    </div>
  );
}
